<!-- Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved. -->

<template>
  <base-modal-comp
    tSel="confirmProfileInfoModal"
    tag="form"
    :title="$t('auth.profileInfo.contactTransfer')"
    :message="$t('auth.profileInfo.pleaseComplete')"
    :hideCloseIcon="false"
    @submit.native.prevent>
    <template #default>
      <div class="spinner" v-if="loadingProfile">
        <spinner-comp size="64"> </spinner-comp>
      </div>
      <profile-info
        v-else
        :model="profileInfoModel"
        :validateOn="validateOn"
        context="profileInfoConfirmation"
        ref="profileInfo"
      >
        <template #submitButton>
          <button-comp
            class="submitButton"
            t-sel="confirmProfileInfoModal-actionBtn"
            legacyOrange
            large
            type="submit"
            @click="submit"
          >
            {{ $t('common.continue') }}
          </button-comp>
        </template>
      </profile-info>
    </template>
  </base-modal-comp>
</template>

<script lang="ts">
import profileInfo from '@/components/auth/profile-info.vue';
import { ProfileInfo } from '@/models/models';
import { getProfileAsync, putConfirmProfileAsync } from '@/api/userApi';
import { isSuccess, isError } from '@/utils/result';
import { isAbortError } from '@/utils/apiUtils';
import { errorToast } from '@/utils/notificationUtils';
import { ValidateOn } from '@protolabs/component-library';

export default {
  name: 'confirm-profile-info-modal',
  components: {
    profileInfo,
  },
  data() {
    return {
      profileInfoModel: new ProfileInfo(),
      loadingProfile: false,
      validateOn: ValidateOn.Submit,
      profileAbortController: new AbortController(),
    };
  },
  async created() {
    try {
      this.loadingProfile = true;
      const token = this.$route?.query?.token;
      const profileResponse = await getProfileAsync(
        token as string,
        this.profileAbortController.signal
      );
      if (isAbortError(profileResponse)) {
        return;
      }
      if (isError(profileResponse)) {
        errorToast(this.$store, 'quoting.errorView.somethingWentWrong');
      } else {
        this.profileInfoModel = profileResponse;
      }
    } finally {
      this.loadingProfile = false;
    }
  },
  beforeUnmount() {
    this.profileAbortController.abort();
  },
  methods: {
    async submit() {
      try {
        const profileInfoRef = this.$refs.profileInfo as typeof profileInfo;

        if (!profileInfoRef) {
          return;
        }
        this.validateOn = ValidateOn.Blur;
        const isValid = await profileInfoRef.validate();

        if (!isValid) {
          return;
        }

        const confirmDto = ProfileInfo
          .toConfirmProfileInfo(this.profileInfoModel);

        const response = await putConfirmProfileAsync(
          confirmDto,
          this.$route.query.token as string
        );

        if (isAbortError(response)) {
          return;
        }

        if (isSuccess(response)) {
          (window as Window).location = this.$route.query.returnUrl as string;
          this.$emit('close');
        } else {
          errorToast(
            this.$store,
            'quoting.errorView.somethingWentWrong'
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.submitButton {
  margin: 30px auto 0px;
}
.header-sub-title {
  color: $gray-med-3;
  font-size: 18px;
}

.spinner {
  text-align: center;
}
</style>
